import { Button } from "@dune/ui";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
	useGetConstructionSiteSubmission,
	useUpdateConstructionSiteSubmissionDocuments,
} from "../../../api/queries/constructionSiteSubmission.queries";
import FileDropUploadAndProcessWithAiInput from "../../../component/constructionSites/FileDropUploadAndProcessWithAiInput";
import { useRequiredParams } from "../../../hooks/useRequiredParams";

interface SubmissionDocumentsProps {
	handlePreviousStep: () => void;
	handleNextStep: () => void;
	isUpdateDisabled?: boolean;
}

export default function SubmissionDocuments({
	handlePreviousStep,
	handleNextStep,
	isUpdateDisabled,
}: SubmissionDocumentsProps) {
	const { constructionSiteId } = useRequiredParams<{
		constructionSiteId: string;
	}>();

	const {
		data: submission,
		isLoading,
		error,
	} = useGetConstructionSiteSubmission(Number(constructionSiteId));

	const { mutate: updateDocuments, isPending: isUpdating } =
		useUpdateConstructionSiteSubmissionDocuments(Number(constructionSiteId));

	const navigate = useNavigate();

	const [currentFileIds, setCurrentFileIds] = useState<string[]>();

	useEffect(() => {
		if (
			currentFileIds === undefined &&
			submission?.folder.files &&
			submission.folder.files.length > 0
		) {
			setCurrentFileIds(submission.folder.files.map((file) => file.id));
		}
	}, [submission, currentFileIds]);

	if (isLoading) {
		return <div>Chargement de la soumission...</div>;
	}

	if (error || !submission) {
		return <div>Erreur lors du chargement de la soumission</div>;
	}

	const existingFileIds = (submission.folder.files || []).map(
		(file) => file.id,
	);

	const handleSave = () => {
		updateDocuments(currentFileIds ?? [], {
			onSuccess: () => {
				navigate(-1);
			},
		});
	};

	const handleSaveAndNext = () => {
		isUpdateDisabled
			? handleNextStep()
			: updateDocuments(currentFileIds ?? [], {
					onSuccess: () => {
						handleNextStep();
					},
				});
	};

	return (
		<div>
			<h2 className="text-xl font-semibold mb-2 text-text-main-blue">
				Déposer les documents du dossier
			</h2>
			<div className="text-gray-500 text-sm mb-6">
				Pièce d'identité, plans, DOC, devis, rapports, attestations décennales…
			</div>

			<FileDropUploadAndProcessWithAiInput
				folderId={submission.folder.id}
				existingFileIds={existingFileIds}
				onUploadedFiles={(fileIds) => {
					setCurrentFileIds((prev) => [...(prev ?? []), ...fileIds]);
				}}
				onDeleteOneFile={(fileId) => {
					setCurrentFileIds((prev) => prev?.filter((id) => id !== fileId));
				}}
				disabled={isUpdateDisabled}
			/>
			<div className="flex justify-between mt-8">
				<Button variant="outline" onClick={handlePreviousStep}>
					Précédent
				</Button>
				<div className="flex gap-2">
					{!isUpdateDisabled && (
						<Button
							variant="outline"
							onClick={handleSave}
							disabled={isUpdating}
						>
							Enregistrer et fermer
						</Button>
					)}
					<Button onClick={handleSaveAndNext} disabled={isUpdating}>
						{isUpdating ? "Sauvegarde..." : "Suivant"}
					</Button>
				</div>
			</div>
		</div>
	);
}
